import React, {
 memo, useCallback, useMemo, useEffect,
 useRef
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useForm, useFieldArray } from 'react-hook-form';

import { Button } from '@common/components/button';
import ModalWindow from '@common/components/modal/modal-window';
import ModalContent from '@common/components/modal/modal-content';
import { Overview } from '@common/components/overview';
import { TextInputHookForm as TextInput } from '@common/components/form/inputs/text';
import { SelectInputHookForm as SelectInput } from '@common/components/form/inputs/select';
import { CustomQuestionModalProps, PulseSurveyQuestionTranslation, TranslationEntry } from '@modules/survey/types/objects';
import { useAppSelector } from '@common/hooks';
import { StoreState } from '@common/types/store';


type FormValues = {
  translations: TranslationEntry[];
};

const CustomQuestionModal = memo(({
  show,
  onHide,
  category,
  onSave,
  loading = false,
  editMode = false,
  questionToEdit
}: CustomQuestionModalProps) => {
  const { t } = useTranslation();
  const hasOpenedRef = useRef(false);
  const trans = useSelector((state: Record<string, any>) => state.language.available);

  const userLocale = useAppSelector((state: StoreState) => state.loggedUser.user.language.locale);

  const getDefaultValues = useCallback((): FormValues => {
    if (editMode && questionToEdit) {
      return {
        translations: questionToEdit.translations.map((translation, index) => ({
          id: `translation-${index}`,
          languageCode: translation.language_code,
          text: translation.text,
        }))
      };
    }
    return {
      translations: [{
        id: `translation-${Date.now()}`,
        languageCode: userLocale,
        text: '',
      }]
    };
  }, [editMode, questionToEdit, userLocale]);

  const {
 control, handleSubmit, formState: { errors }, reset, watch
} = useForm<FormValues>({
    defaultValues: getDefaultValues(),
    mode: 'all'
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'translations'
  });

  const translations = watch('translations');

  useEffect(() => {
    if (show && !hasOpenedRef.current) {
      reset(getDefaultValues());
      hasOpenedRef.current = true;
    }
    if (!show) {
      hasOpenedRef.current = false;
    }
  }, [show, reset, getDefaultValues]);

  const availableLanguages = useMemo(() => {
    const usedLanguageCodes = translations?.map((translation) => translation.languageCode) || [];
    return trans.filter((lang: any) => !usedLanguageCodes.includes(lang.locale));
  }, [trans, translations]);

  const getSelectOptions = useCallback((currentLanguageCode: string) => {
    const availableOptions = availableLanguages.map((lang: any) => ({
      value: lang.locale,
      label: (
        <div className="tw-flex tw-items-center">
          <img
            src={lang.icon}
            alt={lang.name}
            className="tw-w-5 tw-h-4 tw-mr-2 tw-rounded-sm tw-flex-shrink-0"
          />
          <span className="tw-flex-1">{lang.name}</span>
        </div>
      )
    }));

    const currentLanguage = trans.find((lang: any) => lang.locale === currentLanguageCode);
    if (currentLanguage) {
      availableOptions.push({
        value: currentLanguageCode,
        label: (
          <div className="tw-flex tw-items-center">
            <img
              src={currentLanguage.icon}
              alt={currentLanguage.name}
              className="tw-w-5 tw-h-4 tw-mr-2 tw-rounded-sm tw-flex-shrink-0"
            />
            <span className="tw-flex-1">{currentLanguage.name}</span>
          </div>
        )
      });
    }

    return availableOptions;
  }, [availableLanguages, trans]);

  const onSubmit = useCallback(async (data: FormValues) => {
    const validTranslations = data.translations.filter((translation) => translation.text.trim());

    if (validTranslations.length === 0) {
      return;
    }

    const languageCodes = validTranslations.map((translation) => translation.languageCode);
    const uniqueLanguageCodes = Array.from(new Set(languageCodes));

    if (languageCodes.length !== uniqueLanguageCodes.length) {
      return;
    }

    const translationsArray: PulseSurveyQuestionTranslation[] = validTranslations.map((translation) => ({
      language_code: translation.languageCode,
      text: translation.text.trim()
    }));

    const primaryTranslation = translationsArray.find(
      (translation) => translation.language_code === userLocale
    ) || translationsArray[0];


    await onSave({
      text: primaryTranslation.text,
      category,
      translations: translationsArray
    });

    onHide();

  }, [category, onSave, userLocale, onHide]);

  const handleSave = handleSubmit(onSubmit);

  const handleClose = useCallback(() => {
    reset(getDefaultValues());
    onHide();
  }, [onHide, reset, getDefaultValues]);

  const handleAddTranslation = useCallback(() => {
    if (availableLanguages.length === 0) return;

    const newTranslation: TranslationEntry = {
      id: `translation-${Date.now()}`,
      languageCode: availableLanguages[0].locale,
      text: ''
    };

    append(newTranslation);
  }, [availableLanguages, append]);

  const handleRemoveTranslation = useCallback((index: number) => {
    remove(index);
  }, [remove]);

  return (
    <ModalWindow
      show={show}
      onHide={handleClose}
      className="CustomQuestionModal"
    >
      <ModalContent
        title={
          editMode
            ? t('survey:pulse_survey_custom_question_edit_title')
            : t('survey:pulse_survey_custom_question')
        }
        onHide={handleClose}
        hideConfirm
        footerChildren={
          <Button
            type="primary"
            onClick={handleSave}
            isLoading={loading}
          >
            {editMode ? t('survey:pulse_survey_custom_question_update') : t('survey:pulse_survey_custom_question_save')}
          </Button>
        }
      >
        <Overview.Content>
          <div className="tw-mb-6">
            <h3 className="tw-mb-2 tw-text-lg tw-font-semibold">
              {t('survey:pulse_survey_custom_question_translations_title')}
            </h3>
            <p className="tw-mb-4 tw-text-sm tw-text-gray-500">
              {t('survey:pulse_survey_custom_question_translations_description')}
            </p>

            {errors.translations && (
              <div className="tw-text-red-600 tw-text-sm tw-mb-4 tw-p-2 tw-bg-red-50 tw-border tw-border-red-200 tw-rounded">
                {errors.translations.message || t('survey:pulse_survey_custom_question_translations_required')}
              </div>
            )}

            <div className="tw-space-y-4">
              {fields.map((field, index) => (
                <div key={field.id} className="tw-p-4 tw-border tw-border-gray-200 tw-rounded-lg tw-bg-gray-50">
                  <div className="tw-flex tw-justify-between tw-items-center tw-mb-3">
                    <div className={`tw-flex-1 ${fields.length > 1 ? 'tw-mr-4' : ''}`}>
                      <SelectInput
                        name={`translations.${index}.languageCode` as const}
                        control={control}
                        options={getSelectOptions(field.languageCode)}
                        placeholder={t('survey:pulse_survey_custom_question_select_language')}
                        rules={{
                          required: String(t('survey:pulse_survey_custom_question_select_language') || 'Language is required')
                        }}
                        disabled={loading}
                      />
                    </div>
                    {fields.length > 1 && (
                      <Button
                        size="default"
                        type="default"
                        icon="delete"
                        onClick={() => handleRemoveTranslation(index)}
                        className="tw-flex-shrink-0"
                        disabled={loading}
                      />
                    )}
                  </div>
                  <TextInput
                    name={`translations.${index}.text` as const}
                    control={control}
                    placeholder={t('survey:pulse_survey_custom_question_translation_placeholder')}
                    maxLength={250}
                    disabled={loading}
                    rules={{
                      required: index === 0 ?
                        String(t('survey:pulse_survey_custom_question_translations_required') || 'Translation is required') :
                        undefined,
                      validate: {
                        noDuplicateLanguages: (): string | true => {
                          const currentTranslations = translations || [];
                          const languageCodes = currentTranslations
                            .filter((translation) => translation.text.trim())
                            .map((translation) => translation.languageCode);
                          const uniqueLanguageCodes = Array.from(new Set(languageCodes));
                          return languageCodes.length === uniqueLanguageCodes.length ||
                            String(t('survey:pulse_survey_custom_question_duplicate_languages') ||
                              'Duplicate languages not allowed');
                        }
                      }
                    }}
                  />
                </div>
              ))}
            </div>

            <div className="tw-mt-4">
              <Button
                type="primary"
                icon="add"
                onClick={handleAddTranslation}
                disabled={availableLanguages.length === 0 || loading}
              >
                {t('survey:pulse_survey_custom_question_add_translation')}
              </Button>
            </div>
          </div>
        </Overview.Content>
      </ModalContent>
    </ModalWindow>
  );
});

export default CustomQuestionModal;
